"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircle, Fingerprint } from "lucide-react";
import dynamic from "next/dynamic";
import Link from "next/link";
import type React from "react";
import { useState } from "react";
import { AuthHeroPanel } from "@/components/auth/auth-hero-panel";
import { LoginErrorBanner } from "@/components/auth/login/login-error-banner";
import { NoBackupContent } from "@/components/auth/login/no-backup-content";
import { OAuthClientHeader } from "@/components/auth/login/oauth-client-header";
import { SetPasswordContent } from "@/components/auth/login/set-password-content";
import { UnlockContent } from "@/components/auth/login/unlock-content";
import { AuthLegalNotice } from "@/components/auth-legal-notice";
import { AuthNavbar } from "@/components/auth-navbar";
import { ClientThemeProvider } from "@/components/client-theme-provider";
import { RecoveryPhraseRestore } from "@/components/recovery/recovery-phrase-restore";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import { Label } from "@/components/ui/label";
import { type LoginPageServerData, useLoginPage } from "@/hooks/use-login-page";

// The overlay (and its framer-motion dependency) only renders after the user
// submits, so keep it out of the initial route chunk.
const DecryptedTextLoadingOverlay = dynamic(() => import("@/components/decrypted-text-loading-overlay").then(m => m.DecryptedTextLoadingOverlay), {
  ssr: false
});

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: the login card has four mutually exclusive views — set-password, passkey unlock, password unlock and sign-in options — and the branch that picks between them is the page's whole job. All state, effects and handlers already live in useLoginPage; splitting the branch further would only hide which view wins.
function LoginContent(t0) {
  const $ = _c(140);
  const {
    initialClient,
    serverTheme
  } = t0;
  let t1;
  if ($[0] !== initialClient || $[1] !== serverTheme) {
    t1 = {
      initialClient,
      serverTheme
    };
    $[0] = initialClient;
    $[1] = serverTheme;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const login = useLoginPage(t1);
  const [phraseRestoreOpen, setPhraseRestoreOpen] = useState(false);
  const [methodError, setMethodError] = useState(null);
  if (login.signerReturnComplete) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <AuthNavbar />;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10"><CheckCircle className="h-6 w-6 text-primary" /></div>;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="flex min-h-svh flex-col">{t2}<div className="flex flex-1 items-center justify-center p-6 md:p-10"><Card className="w-full max-w-md"><CardContent className="flex flex-col items-center gap-4 p-6 text-center md:p-8">{t3}<div className="space-y-2"><h2 className="font-semibold text-lg">You're signed in</h2><p className="text-muted-foreground text-sm">Return to the app to continue connecting your Sigma wallet.</p></div></CardContent></Card></div></div>;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  const t2 = login.isMac ? "\u2318" : "Ctrl";
  let t3;
  if ($[6] !== t2) {
    t3 = <Kbd>{t2}</Kbd>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Kbd>O</Kbd>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3) {
    t5 = <KbdGroup className="ml-2 hidden sm:inline-flex">{t3}{t4}</KbdGroup>;
    $[9] = t3;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const importHotkey = t5;
  let content;
  if (login.showSetPasswordForm) {
    let t6;
    if ($[11] !== login.confirmPassword || $[12] !== login.handleCancelSetPassword || $[13] !== login.handleSaveImportedWallet || $[14] !== login.loading || $[15] !== login.newPassword || $[16] !== login.setConfirmPassword || $[17] !== login.setNewPassword) {
      t6 = <SetPasswordContent confirmPassword={login.confirmPassword} loading={login.loading} newPassword={login.newPassword} onCancel={login.handleCancelSetPassword} onConfirmPasswordChange={login.setConfirmPassword} onNewPasswordChange={login.setNewPassword} onSubmit={login.handleSaveImportedWallet} />;
      $[11] = login.confirmPassword;
      $[12] = login.handleCancelSetPassword;
      $[13] = login.handleSaveImportedWallet;
      $[14] = login.loading;
      $[15] = login.newPassword;
      $[16] = login.setConfirmPassword;
      $[17] = login.setNewPassword;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    content = t6;
  } else {
    if (login.hasPRFBackup && !login.preferPasswordUnlock && !login.showAllSignInOptions) {
      let t6;
      if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <div className="text-center"><h2 className="font-semibold text-lg">Welcome back</h2><p className="mt-1 text-muted-foreground text-sm">You’re signed in. Unlock your backup to use your identity.</p></div>;
        $[19] = t6;
      } else {
        t6 = $[19];
      }
      const t7 = login.loading || login.prfWorking;
      const t8 = login.prfWorking ? "Unlocking..." : "Unlock with Passkey";
      let t9;
      if ($[20] !== login.handlePRFUnlock || $[21] !== t7 || $[22] !== t8) {
        t9 = <Button className="w-full" disabled={t7} onClick={login.handlePRFUnlock} size="lg">{t8}</Button>;
        $[20] = login.handlePRFUnlock;
        $[21] = t7;
        $[22] = t8;
        $[23] = t9;
      } else {
        t9 = $[23];
      }
      let t10;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>;
        $[24] = t10;
      } else {
        t10 = $[24];
      }
      let t11;
      if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = <div className="relative my-2">{t10}<div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground">or</span></div></div>;
        $[25] = t11;
      } else {
        t11 = $[25];
      }
      let t12;
      if ($[26] !== login.recoveryReady || $[27] !== login.showPasskeyPasswordRecovery) {
        t12 = login.recoveryReady ? <Button className="w-full" onClick={login.showPasskeyPasswordRecovery} size="sm" type="button" variant="outline">Use password instead</Button> : null;
        $[26] = login.recoveryReady;
        $[27] = login.showPasskeyPasswordRecovery;
        $[28] = t12;
      } else {
        t12 = $[28];
      }
      let t13;
      if ($[29] !== login) {
        t13 = () => login.setShowAllSignInOptions(true);
        $[29] = login;
        $[30] = t13;
      } else {
        t13 = $[30];
      }
      let t14;
      if ($[31] !== login.loading || $[32] !== t13) {
        t14 = <Button className="w-full" disabled={login.loading} onClick={t13} size="sm" type="button" variant="ghost">More sign-in options</Button>;
        $[31] = login.loading;
        $[32] = t13;
        $[33] = t14;
      } else {
        t14 = $[33];
      }
      let t15;
      if ($[34] !== t12 || $[35] !== t14) {
        t15 = <div className="space-y-3">{t11}{t12}{t14}</div>;
        $[34] = t12;
        $[35] = t14;
        $[36] = t15;
      } else {
        t15 = $[36];
      }
      let t16;
      if ($[37] !== t15 || $[38] !== t9) {
        t16 = <div className="flex flex-col gap-4">{t6}{t9}{t15}</div>;
        $[37] = t15;
        $[38] = t9;
        $[39] = t16;
      } else {
        t16 = $[39];
      }
      content = t16;
    } else {
      if (login.shouldOfferLocalBackup && !login.showAllSignInOptions) {
        let t6;
        if ($[40] !== login.creatingAccount || $[41] !== login.handleClearBackup || $[42] !== login.handleCreateAccountWithKey || $[43] !== login.handleSubmit || $[44] !== login.loading || $[45] !== login.localWallet || $[46] !== login.oauthClient || $[47] !== login.password || $[48] !== login.setPassword || $[49] !== login.unregisteredKey) {
          t6 = <UnlockContent creatingAccount={login.creatingAccount} loading={login.loading} localWallet={login.localWallet} oauthClient={login.oauthClient} onClearBackup={login.handleClearBackup} onCreateAccountWithKey={login.handleCreateAccountWithKey} onPasswordChange={login.setPassword} onSubmit={login.handleSubmit} password={login.password} unregisteredKey={login.unregisteredKey} />;
          $[40] = login.creatingAccount;
          $[41] = login.handleClearBackup;
          $[42] = login.handleCreateAccountWithKey;
          $[43] = login.handleSubmit;
          $[44] = login.loading;
          $[45] = login.localWallet;
          $[46] = login.oauthClient;
          $[47] = login.password;
          $[48] = login.setPassword;
          $[49] = login.unregisteredKey;
          $[50] = t6;
        } else {
          t6 = $[50];
        }
        content = t6;
      } else {
        let t6;
        if ($[51] !== login) {
          t6 = login.hasPRFBackup && login.showAllSignInOptions ? () => login.setShowAllSignInOptions(false) : null;
          $[51] = login;
          $[52] = t6;
        } else {
          t6 = $[52];
        }
        let t7;
        if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = () => setPhraseRestoreOpen(true);
          $[53] = t7;
        } else {
          t7 = $[53];
        }
        let t8;
        if ($[54] !== importHotkey || $[55] !== login.cancelRecovery || $[56] !== login.emailAuthPending || $[57] !== login.emailForOtp || $[58] !== login.handleEmailOtpSignIn || $[59] !== login.handleRecoveryRetry || $[60] !== login.handleSendEmailOtp || $[61] !== login.handleSocialSignIn || $[62] !== login.handleWalletSignIn || $[63] !== login.isMac || $[64] !== login.loading || $[65] !== login.oauthClient || $[66] !== login.otpCode || $[67] !== login.otpSent || $[68] !== login.recoveryPassword || $[69] !== login.recoveryReady || $[70] !== login.resetEmailOtp || $[71] !== login.setEmailForOtp || $[72] !== login.setOtpCode || $[73] !== login.setOtpSent || $[74] !== login.setRecoveryPassword || $[75] !== login.setShowEmailSignIn || $[76] !== login.showEmailSignIn || $[77] !== login.signupHref || $[78] !== login.socialProviderLoading || $[79] !== login.socialSignInIsPending || $[80] !== login.walletSignInDisabled || $[81] !== t6) {
          t8 = <NoBackupContent cancelRecovery={login.cancelRecovery} emailAuthPending={login.emailAuthPending} emailForOtp={login.emailForOtp} handleEmailOtpSignIn={login.handleEmailOtpSignIn} handleRecoveryRetry={login.handleRecoveryRetry} handleSendEmailOtp={login.handleSendEmailOtp} handleSocialSignIn={login.handleSocialSignIn} handleWalletSignIn={login.handleWalletSignIn} importHotkey={importHotkey} isMac={login.isMac} loading={login.loading} oauthClient={login.oauthClient} onBackToPasskey={t6} onError={setMethodError} onRestorePhrase={t7} otpCode={login.otpCode} otpSent={login.otpSent} recoveryPassword={login.recoveryPassword} recoveryReady={login.recoveryReady} resetEmailOtp={login.resetEmailOtp} setEmailForOtp={login.setEmailForOtp} setOtpCode={login.setOtpCode} setOtpSent={login.setOtpSent} setRecoveryPassword={login.setRecoveryPassword} setShowEmailSignIn={login.setShowEmailSignIn} showEmailSignIn={login.showEmailSignIn} signupHref={login.signupHref} socialProviderLoading={login.socialProviderLoading} socialSignInIsPending={login.socialSignInIsPending} walletSignInDisabled={login.walletSignInDisabled} />;
          $[54] = importHotkey;
          $[55] = login.cancelRecovery;
          $[56] = login.emailAuthPending;
          $[57] = login.emailForOtp;
          $[58] = login.handleEmailOtpSignIn;
          $[59] = login.handleRecoveryRetry;
          $[60] = login.handleSendEmailOtp;
          $[61] = login.handleSocialSignIn;
          $[62] = login.handleWalletSignIn;
          $[63] = login.isMac;
          $[64] = login.loading;
          $[65] = login.oauthClient;
          $[66] = login.otpCode;
          $[67] = login.otpSent;
          $[68] = login.recoveryPassword;
          $[69] = login.recoveryReady;
          $[70] = login.resetEmailOtp;
          $[71] = login.setEmailForOtp;
          $[72] = login.setOtpCode;
          $[73] = login.setOtpSent;
          $[74] = login.setRecoveryPassword;
          $[75] = login.setShowEmailSignIn;
          $[76] = login.showEmailSignIn;
          $[77] = login.signupHref;
          $[78] = login.socialProviderLoading;
          $[79] = login.socialSignInIsPending;
          $[80] = login.walletSignInDisabled;
          $[81] = t6;
          $[82] = t8;
        } else {
          t8 = $[82];
        }
        content = t8;
      }
    }
  }
  let t6;
  if ($[83] !== login.handlePhraseRestored || $[84] !== phraseRestoreOpen) {
    t6 = <RecoveryPhraseRestore onOpenChange={setPhraseRestoreOpen} onRestored={login.handlePhraseRestored} open={phraseRestoreOpen} />;
    $[83] = login.handlePhraseRestored;
    $[84] = phraseRestoreOpen;
    $[85] = t6;
  } else {
    t6 = $[85];
  }
  let t7;
  if ($[86] !== login.isFileDragActive) {
    t7 = login.isFileDragActive ? <div className="pointer-events-none fixed inset-0 z-50 grid place-items-center bg-background/60 backdrop-blur-sm"><div className="rounded-xl border border-dashed bg-background/80 px-6 py-4 text-center shadow-lg"><div className="font-medium">Drop your backup to import</div><div className="mt-1 text-muted-foreground text-xs">Accepts .bep and .json</div></div></div> : null;
    $[86] = login.isFileDragActive;
    $[87] = t7;
  } else {
    t7 = $[87];
  }
  const t8 = login.oauthClient ? login.oauthClient.clientName : undefined;
  let t9;
  if ($[88] !== t8) {
    t9 = <AuthNavbar oauthClientName={t8} />;
    $[88] = t8;
    $[89] = t9;
  } else {
    t9 = $[89];
  }
  let t10;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Label className="sr-only" htmlFor="backup-import">Import backup file</Label>;
    $[90] = t10;
  } else {
    t10 = $[90];
  }
  let t11;
  if ($[91] !== login.handleImportBackup) {
    t11 = <input accept=".json,.bep" className="sr-only" id="backup-import" onChange={login.handleImportBackup} type="file" />;
    $[91] = login.handleImportBackup;
    $[92] = t11;
  } else {
    t11 = $[92];
  }
  let t12;
  if ($[93] !== login.clientProfile || $[94] !== login.loadingClientInfo || $[95] !== login.oauthClient) {
    t12 = <OAuthClientHeader clientProfile={login.clientProfile} loadingClientInfo={login.loadingClientInfo} oauthClient={login.oauthClient} />;
    $[93] = login.clientProfile;
    $[94] = login.loadingClientInfo;
    $[95] = login.oauthClient;
    $[96] = t12;
  } else {
    t12 = $[96];
  }
  let t13;
  if ($[97] !== content || $[98] !== login.handlePasskeySignIn || $[99] !== login.hasPRFBackup || $[100] !== login.loading || $[101] !== login.passkeySignInSupported || $[102] !== login.passkeySignedIn || $[103] !== login.passkeySigningIn || $[104] !== login.showSetPasswordForm || $[105] !== login.storageChecked) {
    t13 = login.storageChecked ? <>{login.passkeySignInSupported && !login.showSetPasswordForm && !login.passkeySignedIn && !login.hasPRFBackup && <Button className="w-full" disabled={login.loading || login.passkeySigningIn} onClick={login.handlePasskeySignIn} size="lg" variant="outline"><Fingerprint className="mr-2 h-4 w-4" />{login.passkeySigningIn ? "Signing in..." : "Sign in with passkey"}</Button>}{login.passkeySignedIn && !login.hasPRFBackup && <p className="text-muted-foreground text-sm">You’re signed in. Use your backup password or restore a backup to access your identity. You can enable passkey unlock in Settings afterward.</p>}{content}</> : null;
    $[97] = content;
    $[98] = login.handlePasskeySignIn;
    $[99] = login.hasPRFBackup;
    $[100] = login.loading;
    $[101] = login.passkeySignInSupported;
    $[102] = login.passkeySignedIn;
    $[103] = login.passkeySigningIn;
    $[104] = login.showSetPasswordForm;
    $[105] = login.storageChecked;
    $[106] = t13;
  } else {
    t13 = $[106];
  }
  let t14;
  if ($[107] !== t12 || $[108] !== t13) {
    t14 = <Card className="w-full max-w-md"><CardContent className="flex flex-col gap-6 p-6 md:p-8">{t12}{t13}</CardContent></Card>;
    $[107] = t12;
    $[108] = t13;
    $[109] = t14;
  } else {
    t14 = $[109];
  }
  const t15 = methodError || login.error || login.prfError;
  let t16;
  if ($[110] !== login.clientProfileError || $[111] !== login.oauthClientError || $[112] !== t15) {
    t16 = <LoginErrorBanner clientProfileError={login.clientProfileError} error={t15} oauthClientError={login.oauthClientError} />;
    $[110] = login.clientProfileError;
    $[111] = login.oauthClientError;
    $[112] = t15;
    $[113] = t16;
  } else {
    t16 = $[113];
  }
  let t17;
  if ($[114] !== t14 || $[115] !== t16) {
    t17 = <div className="flex min-w-0 flex-col p-4 sm:p-6 md:p-10"><div className="flex min-w-0 flex-1 items-center justify-center"><div className="w-full max-w-md">{t14}{t16}</div></div></div>;
    $[114] = t14;
    $[115] = t16;
    $[116] = t17;
  } else {
    t17 = $[116];
  }
  let t18;
  if ($[117] !== login.oauthClient) {
    t18 = <AuthHeroPanel oauthClient={login.oauthClient} />;
    $[117] = login.oauthClient;
    $[118] = t18;
  } else {
    t18 = $[118];
  }
  let t19;
  if ($[119] !== t17 || $[120] !== t18) {
    t19 = <div className="grid min-w-0 flex-1 lg:grid-cols-2">{t17}{t18}</div>;
    $[119] = t17;
    $[120] = t18;
    $[121] = t19;
  } else {
    t19 = $[121];
  }
  let t20;
  let t21;
  if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <span className="min-w-0 text-xs"><AuthLegalNotice /></span>;
    t21 = <span aria-hidden={true} className="hidden sm:inline">·</span>;
    $[122] = t20;
    $[123] = t21;
  } else {
    t20 = $[122];
    t21 = $[123];
  }
  let t22;
  if ($[124] !== login.signupHref) {
    t22 = <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 border-t px-4 py-4 text-center text-muted-foreground text-sm">{t20}{t21}<span className="min-w-0">New to Sigma?{" "}<Link className="underline underline-offset-4" href={login.signupHref} prefetch={true}>Create a wallet</Link></span></div>;
    $[124] = login.signupHref;
    $[125] = t22;
  } else {
    t22 = $[125];
  }
  let t23;
  if ($[126] !== login.showSyncDialog || $[127] !== login.syncSteps) {
    t23 = <DecryptedTextLoadingOverlay open={login.showSyncDialog} steps={login.syncSteps} username="Authenticating" />;
    $[126] = login.showSyncDialog;
    $[127] = login.syncSteps;
    $[128] = t23;
  } else {
    t23 = $[128];
  }
  let t24;
  if ($[129] !== login.clientTheme || $[130] !== t11 || $[131] !== t19 || $[132] !== t22 || $[133] !== t23 || $[134] !== t7 || $[135] !== t9) {
    t24 = <ClientThemeProvider className="flex min-h-svh flex-col" theme={login.clientTheme}>{t7}{t9}{t10}{t11}{t19}{t22}{t23}</ClientThemeProvider>;
    $[129] = login.clientTheme;
    $[130] = t11;
    $[131] = t19;
    $[132] = t22;
    $[133] = t23;
    $[134] = t7;
    $[135] = t9;
    $[136] = t24;
  } else {
    t24 = $[136];
  }
  let t25;
  if ($[137] !== t24 || $[138] !== t6) {
    t25 = <>{t6}{t24}</>;
    $[137] = t24;
    $[138] = t6;
    $[139] = t25;
  } else {
    t25 = $[139];
  }
  return t25;
}

/**
 * Client half of /login. The server page resolves the OAuth client and its
 * theme from `client_id` and hands them in so the branded card paints without
 * a dependent fetch chain (see OPL-4335).
 */
export function LoginPageClient(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <LoginContent {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}